import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../config";
import { kycService } from "../services/kycService";
import ConfirmationModal from "../common/ConfirmationModal";
import {
  FaDownload,
  FaSearchPlus,
  FaSearchMinus,
  FaRedo,
  FaCheck,
  FaTimes,
  FaEye,
} from "react-icons/fa";
import { getImageUrl } from "../utils/docS3";

// Explicitly type the icons as React components
const FaDownloadIcon = FaDownload as React.ComponentType<any>;
const FaSearchPlusIcon = FaSearchPlus as React.ComponentType<any>;
const FaSearchMinusIcon = FaSearchMinus as React.ComponentType<any>;
const FaRedoIcon = FaRedo as React.ComponentType<any>;
const FaCheckIcon = FaCheck as React.ComponentType<any>;
const FaTimesIcon = FaTimes as React.ComponentType<any>;
const FaEyeIcon = FaEye as React.ComponentType<any>;

interface KYCReviewModalProps {
  user: any;
  onClose: () => void;
}

interface KycDocument {
  id: string;
  type: string;
  status: string;
  url: string;
  submitted_at: string;
  ocr_data?: Record<string, string>;
  ocr_status?: string;
}

const rejectionReasons = [
  "Document blurry",
  "Details mismatch",
  "Expired document",
  "PAN not visible",
  "Name mismatch",
  "Address mismatch",
  "Aadhaar number not clear",
  "Bank details incorrect",
  "Other",
];

const documentTypes = {
  "PAN Card": ["PAN Number", "Name on PAN", "Date of Birth"],
  "Aadhar Card": ["Aadhaar Number", "Name", "Date of Birth", "Address"],
  "Bank Statement": ["Bank Account Number", "IFSC Code", "Account Holder Name"],
  "Aadhar Card Front": ["Aadhaar Number", "Name", "Date of Birth"],
  "Aadhar Card Back": ["Address"],
  "Bank Passbook": ["Bank Account Number", "IFSC Code", "Account Holder Name"],
  // Backend document types
  pan_card: ["PAN Number", "Name on PAN", "Date of Birth"],
  aadhaar_card: ["Aadhaar Number", "Name", "Date of Birth", "Address"],
  address_proof: ["Address", "Name", "Date"],
  income_proof: ["Income Amount", "Employer", "Period"],
};

const getDocumentDisplayName = (docType: string): string => {
  const displayNames: { [key: string]: string } = {
    pan_card: "PAN Card",
    aadhaar_card: "Aadhaar Card",
    address_proof: "Address Proof",
    income_proof: "Income Proof",
    "PAN Card": "PAN Card",
    "Aadhar Card": "Aadhaar Card",
    "Bank Statement": "Bank Statement",
    "Aadhar Card Front": "Aadhaar Card Front",
    "Aadhar Card Back": "Aadhaar Card Back",
    "Bank Passbook": "Bank Passbook",
  };
  return displayNames[docType] || docType;
};

export default function KYCReviewModal({ user, onClose }: KYCReviewModalProps) {
  const [documents, setDocuments] = useState<KycDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [status, setStatus] = useState(user.kyc_status);
  const [successMsg, setSuccessMsg] = useState("");

  // Image viewer state
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageRotation, setImageRotation] = useState(0);

  // Confirmation modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);

  // useEffect(() => {
  //   // Fetch KYC documents for the user
  //   async function fetchDocs() {
  //     setLoading(true);
  //     setError("");
  //     try {
  //       const response = await kycService.getKYCDocuments(user.id);
  //       // Map backend response to frontend format
  //       const mappedDocuments = (response.kyc_documents || []).map(
  //         (doc: any, index: number) => ({
  //           id: doc._id || `doc-${index}`,
  //           type: doc.document_type || "Unknown Type",
  //           status: "uploaded",
  //           url: doc.file_url ? doc.file_url : "",
  //           submitted_at: doc.uploaded_at || response.submitted_at || "",
  //           ocr_data: doc.ocr_data || {},
  //           ocr_status: doc.ocr_status || "pending",
  //         })
  //       );
  //       setDocuments(mappedDocuments);
  //     } catch (err) {
  //       setError("Failed to fetch KYC documents");
  //     } finally {
  //       setLoading(false);
  //     }
  //   }
  //   fetchDocs();
  // }, [user.id]);
  useEffect(() => {
    async function fetchDocs() {
      setLoading(true);
      setError("");
      try {
        const response = await kycService.getKYCDocuments(user.id);

        const mappedDocuments = await Promise.all(
          (response.kyc_documents || []).map(
            async (doc: any, index: number) => {
              let signedUrl = "";
              if (doc.file_url) {
                // remove leading slash agar ho
                const key = doc.file_url.startsWith("/")
                  ? doc.file_url.slice(1)
                  : doc.file_url;

                signedUrl = await getImageUrl(key);
              }

              return {
                id: doc._id || `doc-${index}`,
                type: doc.document_type || "Unknown Type",
                status: "uploaded",
                url: signedUrl || "", // signed url yaha set hoga
                submitted_at: doc.uploaded_at || response.submitted_at || "",
                ocr_data: doc.ocr_data || {},
                ocr_status: doc.ocr_status || "pending",
              };
            }
          )
        );

        setDocuments(mappedDocuments);
      } catch (err) {
        setError("Failed to fetch KYC documents");
      } finally {
        setLoading(false);
      }
    }
    fetchDocs();
  }, [user.id]);

  const handleApprove = () => {
    setShowApproveModal(true);
  };

  const handleReject = () => {
    if (!rejectionReason && !customReason) {
      setError("Please select or enter a rejection reason.");
      return;
    }
    setShowRejectModal(true);
  };

  const handleConfirmApprove = async () => {
    try {
      setApproveLoading(true);
      setError("");
      await kycService.approveKYC(user.id);
      setStatus("verified");
      setSuccessMsg("KYC Approved! User will be notified.");
      setShowApproveModal(false);
      setTimeout(onClose, 1200);
    } catch (err) {
      setError("Failed to approve KYC");
    } finally {
      setApproveLoading(false);
    }
  };

  const handleConfirmReject = async () => {
    try {
      setRejectLoading(true);
      setError("");
      const reason =
        rejectionReason === "Other" ? customReason : rejectionReason;
      await kycService.rejectKYC(user.id, reason, adminNotes);
      setStatus("rejected");
      setSuccessMsg("KYC Rejected. User will be notified.");
      setShowRejectModal(false);
      setTimeout(onClose, 1200);
    } catch (err) {
      setError("Failed to reject KYC");
    } finally {
      setRejectLoading(false);
    }
  };

  const handleCancelApprove = () => {
    setShowApproveModal(false);
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
  };

  const openImageViewer = (url: string) => {
    setSelectedImage(url);
    setImageZoom(1);
    setImageRotation(0);
  };

  const closeImageViewer = () => {
    setSelectedImage(null);
  };

  const zoomIn = () => setImageZoom((prev) => Math.min(prev + 0.25, 3));
  const zoomOut = () => setImageZoom((prev) => Math.max(prev - 0.25, 0.5));
  const rotateImage = () => setImageRotation((prev) => (prev + 90) % 360);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "verified":
        return <FaCheckIcon className="text-green-600" />;
      case "failed":
        return <FaTimesIcon className="text-red-600" />;
      default:
        return <FaEyeIcon className="text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "verified":
        return "bg-green-100 text-green-700";
      case "failed":
        return "bg-red-100 text-red-700";
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };
  console.log(documents, "=========documents=======");
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full p-6 overflow-y-auto max-h-[90vh]">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <h2 className="text-2xl font-bold text-blue-700">
              Review KYC for: {user.full_name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl p-2 hover:bg-gray-100 rounded"
            >
              ✕
            </button>
          </div>

          {/* User Basic Info */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-3 text-gray-800">
              User Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="font-medium text-gray-600">User ID:</div>
                <div className="text-gray-800">{user.id}</div>
              </div>
              <div>
                <div className="font-medium text-gray-600">Email:</div>
                <div className="text-gray-800">{user.email}</div>
              </div>
              <div>
                <div className="font-medium text-gray-600">Mobile:</div>
                <div className="text-gray-800">{user.mobile || "N/A"}</div>
              </div>
            </div>
            <div className="mt-3">
              <div className="font-medium text-gray-600">
                Current KYC Status:
              </div>
              <div
                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  status
                )}`}
              >
                {getStatusIcon(status)}
                {status}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4 border border-red-200">
              {error}
            </div>
          )}
          {successMsg && (
            <div className="bg-green-100 text-green-700 p-3 rounded mb-4 border border-green-200">
              {successMsg}
            </div>
          )}

          {/* Document Display Area */}
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <div className="text-gray-600">Loading documents...</div>
            </div>
          ) : (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg text-gray-800 mb-4">
                Submitted Documents
              </h3>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📄</div>
                  <div>No documents submitted yet</div>
                </div>
              ) : (
                documents.map((doc) => {
                  const isPdf =
                    doc.url && doc.url.toLowerCase().endsWith(".pdf");
                  const hasUrl = !!doc.url;
                  const docType = getDocumentDisplayName(doc.type);
                  const expectedFields =
                    documentTypes[doc.type as keyof typeof documentTypes] || [];
                  console.log(doc.url, "===========doc.url==========");
                  return (
                    <div
                      key={doc.id}
                      className="border rounded-lg p-6 bg-white shadow-sm"
                    >
                      {/* Document Header */}
                      <div className="flex flex-wrap gap-4 items-center mb-4 pb-3 border-b">
                        <div className="font-semibold text-lg text-gray-800">
                          {docType}
                        </div>
                        <div
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                            doc.status
                          )}`}
                        >
                          {getStatusIcon(doc.status)}
                          {doc.status || "Unknown"}
                        </div>
                        <div className="text-sm text-gray-500">
                          Submitted:{" "}
                          {doc.submitted_at
                            ? new Date(doc.submitted_at).toLocaleDateString()
                            : "N/A"}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Image Viewer */}
                        <div className="flex flex-col items-center">
                          <div className="font-medium text-gray-700 mb-3">
                            Document Image
                          </div>
                          {hasUrl ? (
                            isPdf ? (
                              <div className="w-64 h-40 flex items-center justify-center bg-gray-200 text-gray-500 border rounded-lg mb-3">
                                <div className="text-center">
                                  <div className="text-4xl mb-2">📄</div>
                                  <div className="text-sm">PDF Document</div>
                                </div>
                              </div>
                            ) : (
                              <div className="relative w-64 h-40 mb-3">
                                <img
                                  src={doc.url}
                                  alt={docType}
                                  className="w-64 h-40 object-contain border rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => openImageViewer(doc.url)}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/pdf-icon.png";
                                  }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
                                  <span
                                    className="text-lg font-bold text-gray-500 opacity-40 rotate-[-20deg] whitespace-nowrap"
                                    style={{ userSelect: "none" }}
                                  >
                                    TPL Admin Panel - Confidential
                                  </span>
                                </div>
                              </div>
                            )
                          ) : (
                            <div className="w-64 h-40 flex items-center justify-center bg-gray-200 text-gray-500 border rounded-lg mb-3">
                              <div className="text-center">
                                <div className="text-4xl mb-2">❌</div>
                                <div className="text-sm">
                                  No document uploaded
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Document Actions */}
                          <div className="flex gap-2">
                            {hasUrl && (
                              <>
                                <button
                                  onClick={() => openImageViewer(doc.url)}
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm px-2 py-1 rounded hover:bg-blue-50"
                                  title="View Full Size"
                                >
                                  <FaEyeIcon />
                                  View
                                </button>
                                <a
                                  href={doc.url}
                                  download
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm px-2 py-1 rounded hover:bg-green-50"
                                  title="Download Original"
                                >
                                  <FaDownloadIcon />
                                  Download
                                </a>
                              </>
                            )}
                          </div>
                        </div>

                        {/* OCR Data Display */}
                        <div className="flex flex-col">
                          <div className="font-medium text-gray-700 mb-3">
                            Extracted Data (OCR)
                          </div>
                          {doc.ocr_data ? (
                            <div className="bg-gray-50 border rounded-lg p-4">
                              {expectedFields.length > 0 ? (
                                <div className="space-y-2">
                                  {expectedFields.map((field) => (
                                    <div
                                      key={field}
                                      className="flex justify-between items-center py-1 border-b border-gray-200 last:border-b-0"
                                    >
                                      <span className="font-medium text-gray-600 text-sm">
                                        {field}:
                                      </span>
                                      <span className="text-gray-800 text-sm">
                                        {doc.ocr_data?.[field] ||
                                          "Not detected"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  {Object.entries(doc.ocr_data || {}).map(
                                    ([key, value]) => (
                                      <div
                                        key={key}
                                        className="flex justify-between items-center py-1 border-b border-gray-200 last:border-b-0"
                                      >
                                        <span className="font-medium text-gray-600 text-sm">
                                          {key}:
                                        </span>
                                        <span className="text-gray-800 text-sm">
                                          {value}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="bg-gray-50 border rounded-lg p-4 text-center text-gray-500 text-sm">
                              No OCR data available
                            </div>
                          )}

                          {/* OCR Status */}
                          {doc.ocr_status && (
                            <div
                              className={`mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                                doc.ocr_status
                              )}`}
                            >
                              {getStatusIcon(doc.ocr_status)}
                              OCR: {doc.ocr_status}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Admin Review Notes */}
          <div className="mt-8 border-t pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Review Notes (Internal)
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add any internal notes for audit log or future reference..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
            <button
              onClick={handleApprove}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={actionLoading || status === "verified"}
            >
              <FaCheckIcon />
              Approve KYC
            </button>
            <button
              onClick={() => setShowReject(!showReject)}
              className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={actionLoading || status === "rejected"}
            >
              <FaTimesIcon />
              Reject KYC
            </button>
          </div>

          {/* Rejection Section */}
          {showReject && (
            <div className="mt-6 border-t pt-6 bg-red-50 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-3">
                Rejection Details
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejection Reason
                  </label>
                  <select
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="">Select a reason</option>
                    {rejectionReasons.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                {rejectionReason === "Other" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Reason
                    </label>
                    <input
                      type="text"
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Enter custom rejection reason"
                    />
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    onClick={handleReject}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={actionLoading}
                  >
                    Confirm Rejection
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden">
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button
                onClick={zoomIn}
                className="bg-white bg-opacity-80 p-2 rounded-full hover:bg-opacity-100"
                title="Zoom In"
              >
                <FaSearchPlusIcon />
              </button>
              <button
                onClick={zoomOut}
                className="bg-white bg-opacity-80 p-2 rounded-full hover:bg-opacity-100"
                title="Zoom Out"
              >
                <FaSearchMinusIcon />
              </button>
              <button
                onClick={rotateImage}
                className="bg-white bg-opacity-80 p-2 rounded-full hover:bg-opacity-100"
                title="Rotate"
              >
                <FaRedoIcon />
              </button>
              <button
                onClick={closeImageViewer}
                className="bg-white bg-opacity-80 p-2 rounded-full hover:bg-opacity-100"
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex items-center justify-center p-8">
              <img
                src={selectedImage}
                alt="Document"
                className="max-w-full max-h-full object-contain"
                style={{
                  transform: `scale(${imageZoom}) rotate(${imageRotation}deg)`,
                  transition: "transform 0.2s ease-in-out",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Approve KYC Confirmation Modal */}
      <ConfirmationModal
        isOpen={showApproveModal}
        onConfirm={handleConfirmApprove}
        onCancel={handleCancelApprove}
        title="Approve KYC Verification"
        message={`Are you sure you want to approve KYC verification for ${user.full_name}?`}
        confirmText="Approve"
        confirmButtonColor="bg-blue-600 hover:bg-blue-700"
        loading={approveLoading}
      />

      {/* Reject KYC Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRejectModal}
        onConfirm={handleConfirmReject}
        onCancel={handleCancelReject}
        title="Reject KYC Verification"
        message={`Are you sure you want to reject KYC verification for ${user.full_name}?`}
        confirmText="Reject"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        loading={rejectLoading}
      />
    </>
  );
}
